(() => {
  const DATA = window.TPS_RACKET_FINDER_DATA || {};
  const catalog = Array.isArray(DATA.rackets) ? DATA.rackets : [];

  const questions = [
    {
      key: "experience",
      title: "Which sounds most like you?",
      help: "This helps us judge how much racket sport experience you already bring.",
      options: [
        ["new", "Completely new", "I am new to padel and racket sports", "☺"],
        ["transfer", "I have racket sport experience", "Tennis, squash, badminton or a similar sport", "↔"],
        ["padel", "I have played some padel", "I know the basics and want the right first racket", "◉"]
      ]
    },
    {
      key: "goal",
      title: "What would you most like your racket to help with?",
      help: "Choose the outcome that sounds most useful for your first few months.",
      options: [
        ["control", "Control and confidence", "Make clean contact and place the ball more easily", "◎"],
        ["balanced", "A bit of everything", "A versatile racket that can grow with my game", "◇"],
        ["power", "Easy power", "Help me add speed without forcing every shot", "⚡"]
      ]
    },
    {
      key: "feel",
      title: "What should it feel like in your hand?",
      help: "No technical knowledge needed. Just choose what sounds most comfortable.",
      options: [
        ["light", "Light and quick", "Easy to move and prepare for the next shot", "↗"],
        ["comfort", "Comfort first", "A softer response that is kinder to my arm", "♡"],
        ["solid", "Solid and steady", "More stability when the ball arrives with pace", "▣"]
      ]
    }
  ];

  const profiles = {
    comfort: {
      title: "Comfortable control",
      lead: "A forgiving racket with a softer response should help you learn confidently without making your arm work too hard.",
      chips: ["Large sweet spot", "Low or even balance", "Soft to medium feel"]
    },
    light: {
      title: "Quick confidence",
      lead: "A lighter, easy moving racket should make preparation simpler and help you react comfortably around the court.",
      chips: ["Light handling", "Round or teardrop shape", "Low or even balance"]
    },
    power: {
      title: "Accessible power",
      lead: "Your previous experience should let you enjoy a more responsive racket while keeping the setup manageable.",
      chips: ["Teardrop shape", "Even balance", "Medium feel"]
    },
    versatile: {
      title: "Easy versatility",
      lead: "A balanced, forgiving racket should give you control while leaving plenty of room for your game to develop.",
      chips: ["Medium weight", "Even balance", "Round or teardrop shape"]
    },
    control: {
      title: "Forgiving control",
      lead: "A calm, predictable racket should help you find the middle more often and build confidence with every session.",
      chips: ["Large sweet spot", "Round shape", "Soft to medium feel"]
    }
  };

  function finderTagValues(tags, key) {
    const prefix = `finder:${key}:`;
    return (tags || [])
      .filter(tag => String(tag).toLowerCase().startsWith(prefix))
      .map(tag => String(tag).slice(prefix.length).trim().toLowerCase())
      .filter(Boolean);
  }

  function buildAvailableRackets(products) {
    const byHandle = new Map(catalog.map(racket => [racket.handle, racket]));

    return (products || []).map(product => {
      const existing = byHandle.get(product.handle);
      if (existing) {
        return {
          ...existing,
          name: product.name || existing.name,
          image: product.image || existing.image,
          url: product.url || existing.url,
          price: Number(product.price || 0),
          compareAtPrice: Number(product.compareAtPrice || 0),
          available: Boolean(product.available)
        };
      }

      const attributes = {
        level: finderTagValues(product.tags, "level")[0],
        style: finderTagValues(product.tags, "style")[0],
        play: finderTagValues(product.tags, "play")[0],
        weight: finderTagValues(product.tags, "weight")[0],
        balance: finderTagValues(product.tags, "balance")[0],
        shape: finderTagValues(product.tags, "shape")[0],
        feel: finderTagValues(product.tags, "feel")[0]
      };

      if (Object.values(attributes).some(value => !value)) return null;

      return {
        ...attributes,
        name: product.name,
        handle: product.handle,
        image: product.image,
        url: product.url || `/products/${product.handle}`,
        price: Number(product.price || 0),
        compareAtPrice: Number(product.compareAtPrice || 0),
        available: Boolean(product.available),
        comfortTech: finderTagValues(product.tags, "comfort-tech")[0] === "true"
      };
    }).filter(racket => racket && racket.available && racket.price > 0);
  }

  function scoreRacket(racket, answers) {
    let score = 0;

    const experienceScores = {
      new: { developing: 44, intermediate: 7, advanced: -28 },
      transfer: { developing: 28, intermediate: 31, advanced: -12 },
      padel: { developing: 20, intermediate: 34, advanced: -4 }
    };
    score += experienceScores[answers.experience]?.[racket.level] || 0;

    if (answers.goal === "control") {
      if (racket.play === "control") score += 30;
      if (racket.play === "control-power") score += 11;
      if (["round", "teardrop"].includes(racket.shape)) score += 6;
      if (["head-light", "even"].includes(racket.balance)) score += 5;
    }

    if (answers.goal === "balanced") {
      if (racket.play === "control-power") score += 30;
      if (racket.style === "all-rounder") score += 10;
      if (racket.shape === "teardrop") score += 7;
      if (racket.balance === "even") score += 7;
    }

    if (answers.goal === "power") {
      if (racket.play === "power") score += 29;
      if (racket.play === "control-power") score += 12;
      if (["teardrop", "diamond"].includes(racket.shape)) score += 5;
    }

    if (answers.feel === "light") {
      if (racket.weight === "super-light") score += 25;
      if (racket.weight === "light") score += 21;
      if (racket.weight === "medium") score += 5;
      if (racket.weight === "heavy") score -= 14;
      if (racket.balance === "head-light") score += 9;
    }

    if (answers.feel === "comfort") {
      if (racket.feel === "soft") score += 25;
      if (racket.feel === "medium") score += 7;
      if (racket.feel === "firm") score -= 18;
      if (racket.comfortTech) score += 12;
      if (racket.balance === "head-heavy") score -= 9;
      if (racket.weight === "heavy") score -= 10;
    }

    if (answers.feel === "solid") {
      if (racket.weight === "medium") score += 17;
      if (racket.weight === "heavy") score += 9;
      if (racket.balance === "even") score += 10;
      if (racket.feel === "medium") score += 8;
      if (racket.feel === "firm" && answers.experience !== "new") score += 5;
    }

    if (answers.experience === "new") {
      if (racket.balance === "head-heavy") score -= 13;
      if (["diamond", "geometric"].includes(racket.shape)) score -= 12;
      if (racket.feel === "firm") score -= 13;
      if (racket.weight === "heavy") score -= 15;
    }

    if (answers.experience === "transfer") {
      if (racket.feel === "firm") score -= 5;
      if (racket.weight === "heavy") score -= 6;
    }

    return score;
  }

  function chooseProfile(answers) {
    if (answers.feel === "comfort") return profiles.comfort;
    if (answers.feel === "light") return profiles.light;
    if (answers.goal === "power" && answers.experience !== "new") return profiles.power;
    if (answers.goal === "balanced") return profiles.versatile;
    return profiles.control;
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat("en-NZ", {
      style: "currency",
      currency: "NZD",
      maximumFractionDigits: 0
    }).format(cents / 100);
  }

  function reasonFor(racket, answers) {
    const reasons = [];
    if (answers.goal === "control" && racket.play === "control") reasons.push("forgiving control");
    if (answers.goal === "balanced" && racket.play === "control-power") reasons.push("balanced performance");
    if (answers.goal === "power" && ["power", "control-power"].includes(racket.play)) reasons.push("accessible power");
    if (answers.feel === "light" && ["super-light", "light"].includes(racket.weight)) reasons.push("quick handling");
    if (answers.feel === "comfort" && racket.feel === "soft") reasons.push("a softer response");
    if (answers.feel === "solid" && ["medium", "heavy"].includes(racket.weight)) reasons.push("useful stability");
    if (!reasons.length) reasons.push("your starting point and playing priorities");
    return `A strong fit for ${reasons.slice(0, 2).join(" and ")}.`;
  }

  class ThePadelShopQuickRacketMatch extends HTMLElement {
    connectedCallback() {
      if (this.shadowRoot) return;

      let products = [];
      try {
        products = JSON.parse(this.querySelector("[data-tps-quick-products]")?.textContent || "[]");
      } catch {
        products = [];
      }

      const template = this.querySelector("template");
      if (!template) return;

      const root = this.attachShadow({ mode: "open" });
      root.appendChild(template.content.cloneNode(true));
      this.initialise(root, buildAvailableRackets(products));
    }

    initialise(root, rackets) {
      const host = root.querySelector("#quickHost");
      const stepLabel = root.querySelector("#stepLabel");
      const progressBar = root.querySelector("#progressBar");
      const backButton = root.querySelector("#backButton");
      const shell = root.querySelector(".quick-shell");
      const minBudget = Number(this.dataset.minBudget || 150);
      const maxBudget = Number(this.dataset.maxBudget || 600);
      const budgetStep = Number(this.dataset.budgetStep || 25);
      const fullFinderUrl = this.dataset.fullFinderUrl || "/pages/racket-finder";
      const browseUrl = this.dataset.browseUrl || "/collections/all-padel-rackets";

      let current = 0;
      let answers = {};

      const rankRackets = () => rackets
        .map(racket => ({ ...racket, score: scoreRacket(racket, answers) }))
        .sort((a, b) => b.score - a.score || a.price - b.price);

      const productCard = (item, index) => {
        const racket = item.racket;
        const comparePrice = racket.compareAtPrice > racket.price
          ? `<span class="compare-price">${formatMoney(racket.compareAtPrice)}</span>`
          : "";
        const visual = racket.image
          ? `<img src="${racket.image}" alt="${racket.name}" loading="lazy">`
          : `<span class="racket-placeholder">Product image</span>`;

        return `
          <article class="racket-card ${index === 0 ? "is-best" : ""}">
            <span class="card-label">${item.label}</span>
            <div class="racket-visual">${visual}</div>
            <h4>${racket.name}</h4>
            <p class="racket-price">${formatMoney(racket.price)}${comparePrice}</p>
            <p class="racket-reason">${reasonFor(racket, answers)}</p>
            <a class="card-link" href="${racket.url}">View racket <span aria-hidden="true">→</span></a>
          </article>`;
      };

      const selectMatches = budget => {
        const ranked = rankRackets();
        if (!ranked.length) return { items: [], status: "" };

        const unlimited = budget >= maxBudget;
        if (unlimited) {
          const best = ranked[0];
          const valuePool = ranked.filter((racket, index) => index > 0 && racket.score >= best.score - 12);
          const bestValue = valuePool.sort((a, b) => a.price - b.price || b.score - a.score)[0];
          const chosen = [best, bestValue].filter(Boolean);
          const alternative = ranked.find(racket => !chosen.includes(racket));
          if (alternative) chosen.push(alternative);
          const labels = ["BEST MATCH", "BEST VALUE", "ALTERNATIVE"];
          return {
            items: chosen.map((racket, index) => ({ racket, label: labels[index] })),
            status: "Showing your strongest suitable matches."
          };
        }

        const ceiling = budget * 100;
        const within = ranked.filter(racket => racket.price <= ceiling);
        const above = ranked.filter(racket => racket.price > ceiling);

        if (!within.length) {
          const bestScore = ranked[0].score;
          const nearest = above
            .filter(racket => racket.score >= bestScore - 15)
            .sort((a, b) => a.price - b.price || b.score - a.score)[0] || ranked[0];
          return {
            items: [{ racket: nearest, label: "CLOSEST SUITABLE OPTION" }],
            status: `No suitable racket is currently available at $${budget} or less. The closest suitable option is ${formatMoney(nearest.price)}.`
          };
        }

        const best = within[0];
        const valuePool = within.filter((racket, index) => index > 0 && racket.score >= best.score - 12);
        const bestValue = valuePool.sort((a, b) => a.price - b.price || b.score - a.score)[0];
        const chosen = [best, bestValue].filter(Boolean);
        const stepUp = above.find(racket => racket.score >= best.score + 6);
        const alternative = within.find(racket => !chosen.includes(racket));
        const third = stepUp || alternative;
        if (third) chosen.push(third);

        return {
          items: chosen.map((racket, index) => ({
            racket,
            label: index === 0
              ? `BEST MATCH UNDER $${budget}`
              : index === 1
                ? "BEST VALUE"
                : stepUp && racket === stepUp
                  ? "WORTH THE STEP UP"
                  : "ALTERNATIVE"
          })),
          status: `Showing the strongest suitable rackets priced at $${budget} or less${stepUp ? ", plus one meaningful step up" : ""}.`
        };
      };

      const renderRecommendations = budget => {
        const grid = root.querySelector("#recommendationGrid");
        const status = root.querySelector("#budgetStatus");
        const selection = selectMatches(budget);
        grid.innerHTML = selection.items.map(productCard).join("");
        status.textContent = selection.status;
      };

      const renderQuestion = () => {
        shell.classList.remove("is-results");
        const question = questions[current];
        stepLabel.textContent = `Question ${current + 1} of 3`;
        progressBar.style.width = `${((current + 1) / 3) * 100}%`;
        backButton.hidden = current === 0;
        host.innerHTML = `
          <div class="question">
            <h3>${question.title}</h3>
            <p class="question-help">${question.help}</p>
            <div class="options">
              ${question.options.map(([value, label, detail, icon]) => `
                <button type="button" class="option" data-value="${value}">
                  <span class="option-icon" aria-hidden="true">${icon}</span>
                  <span class="option-copy"><strong>${label}</strong><small>${detail}</small></span>
                  <span class="option-arrow" aria-hidden="true">›</span>
                </button>`).join("")}
            </div>
          </div>`;

        host.querySelectorAll("[data-value]").forEach(button => {
          button.addEventListener("click", () => {
            answers[question.key] = button.dataset.value;
            if (current < questions.length - 1) {
              current += 1;
              renderQuestion();
            } else {
              renderResults();
            }
          });
        });
      };

      const renderResults = () => {
        shell.classList.add("is-results");
        stepLabel.textContent = "Your quick match";
        progressBar.style.width = "100%";
        backButton.hidden = true;

        if (!rackets.length) {
          host.innerHTML = `
            <div class="result">
              <h3>Choose your racket collection</h3>
              <p class="result-lead">Open this section in the theme editor and select the same racket collection used by the full Racket Finder.</p>
              <div class="empty-state">Once selected, current Shopify prices, images and availability will appear here automatically.</div>
            </div>`;
          return;
        }

        const profile = chooseProfile(answers);
        host.innerHTML = `
          <div class="result">
            <h3>${profile.title}</h3>
            <p class="result-lead">${profile.lead}</p>
            <div class="result-chips">${profile.chips.map(chip => `<span class="result-chip">${chip}</span>`).join("")}</div>
            <div class="budget-panel">
              <div class="budget-head">
                <label for="budgetRange">Set your maximum budget</label>
                <span class="budget-value" id="budgetValue">Any budget</span>
              </div>
              <input class="budget-range" id="budgetRange" type="range" min="${minBudget}" max="${maxBudget}" step="${budgetStep}" value="${maxBudget}" aria-describedby="budgetStatus">
              <div class="range-labels" aria-hidden="true"><span>$${minBudget}</span><span>$${Math.round((minBudget + maxBudget) / 2 / 25) * 25}</span><span>Any budget</span></div>
              <p class="budget-status" id="budgetStatus"></p>
            </div>
            <div class="recommendation-grid" id="recommendationGrid" aria-live="polite"></div>
            <div class="result-actions">
              <button type="button" class="secondary-button" id="restartButton">Start again</button>
              <a class="primary-link" href="${fullFinderUrl}">Take the full finder <span aria-hidden="true">→</span></a>
              <a class="secondary-button" href="${browseUrl}">Browse all rackets</a>
            </div>
          </div>`;

        const budgetRange = root.querySelector("#budgetRange");
        const budgetValue = root.querySelector("#budgetValue");
        const updateBudget = () => {
          const value = Number(budgetRange.value);
          budgetValue.textContent = value >= maxBudget ? "Any budget" : `Up to $${value}`;
          renderRecommendations(value);
        };
        budgetRange.addEventListener("input", updateBudget);
        root.querySelector("#restartButton").addEventListener("click", () => {
          current = 0;
          answers = {};
          renderQuestion();
        });
        updateBudget();
      };

      backButton.addEventListener("click", () => {
        if (current > 0) {
          current -= 1;
          renderQuestion();
        }
      });

      renderQuestion();
    }
  }

  if (!customElements.get("the-padel-shop-quick-racket-match")) {
    customElements.define("the-padel-shop-quick-racket-match", ThePadelShopQuickRacketMatch);
  }
})();